import React from 'react';
import SectionHeading from '../components/ui/SectionHeading';
import AdvantageCard from '../components/ui/AdvantageCard';
import { Users, Clock, ShieldCheck, DollarSign, FileText, Headphones } from 'lucide-react';

const Advantages: React.FC = () => {
  const advantages = [
    {
      icon: Users,
      title: 'Customer-Focused Approach',
      description: 'We emphasize your unique needs and goals, tailoring our property management services to optimize profitability and effectively grow your real estate portfolio.'
    },
    {
      icon: Clock,
      title: 'Same to Next Day Response',
      description: 'Quick response times to both owner and tenant inquiries, ensuring issues are addressed promptly and without delays that can cause further issues.'
    },
    {
      icon: ShieldCheck,
      title: 'Vetted Vendors',
      description: 'We work only with reliable, licensed, and insured maintenance professionals and service providers, many who we have known for years or decades.'
    },
    {
      icon: DollarSign,
      title: 'Straightforward Pricing',
      description: 'Receipts for repairs and renovation are thoroughly documented and invoiced without markups or kickbacks on our end. Pricing is clearly communicated.'
    },
    {
      icon: FileText,
      title: 'Digital Documentation System',
      description: 'Secure, accessible digital records of documents, reports, and communications. We offer a search functionality and AI summaries to optimize efficiency.'
    },
    {
      icon: Headphones,
      title: 'Tech-Forward Support',
      description: 'Round-the-clock availability by text and in-app alerts that show availablity for urgent property issues, ensuring comfort for both owners and tenants. '
    }
  ];

  return (
    <section id="services" className="py-20 bg-gradient-to-b from-[#f0fdbc]/30 via-[#f0fdbc]/30 to-[#f0fdbc]/20 relative">
      <div className="absolute inset-x-0 top-0 h-32 bg-gradient-to-b from-background to-transparent"></div>
      <div className="absolute inset-x-0 bottom-0 h-32 bg-gradient-to-t from-[#FFF8ED] to-transparent"></div>
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        <SectionHeading 
          title="The RentSparks Advantage" 
          subtitle="Whether your income property is residential or commercial, our overarching intention is cultivating long-term relationships with you to seamlessly grow your real estate portfolio with us."
        />
        
        <div className="max-w-4xl mx-auto mb-12 space-y-6 text-left">
          <p className="text-dark-gray text-lg leading-relaxed">
            <strong>We believe tech-enabled operational excellence and authentic care for both residents and property owners leads to an optimal experience. Discover below why property owners choose us for professional management services.</strong>
          </p>
          <p className="text-dark-gray text-lg leading-relaxed">
            We specialize in single family rentals (SFRs) and multifamily housing. We also have experience in managing office units, including flex space and medical offices. 
          </p>
          <p className="text-dark-gray text-lg leading-relaxed">
            <strong>With us, rent and other income goes directly to your bank account, which you can rename to your business entity or DBA name for privacy purposes if desired. We are happy to help establish these accounts and systems free of charge while you retain login credentials, not including any state and filing fees. Repair requests and invoices are communicated to you promptly and diligently recorded for good bookkeeping. We believe this simple setup enhances transparency and decreases liability for all parties.</strong>
          </p>
          
          <p className="text-dark-gray text-lg leading-relaxed">
            We are currently serving Los Angeles, Orange, San Bernardino, and Ventura Counties. We are always looking to expand and are currently evaluating the Central Coast and the Bay Area. If you'd like your zip code to be covered, drop us a line! We have experienced real estate professionals in our network across the country and will see how to best help, whether that's by referral or having you on board. 
          </p>
          
          <div className="mt-10 rounded-2xl bg-emerald-50 border border-emerald-200/60 p-6 shadow-sm">
            <p className="text-emerald-900 text-lg leading-relaxed">
              <strong>Looking for a vetted vendor in the LA metro area?</strong> Whether you need a plumber, a glass re-glazer, a cabinet front installer, or a washer and dryer installation specialist, we've built a curated list of trusted contractors across both niche and basic services. Email us at <a href="mailto:info@rentsparks.com" className="font-semibold text-emerald-700 underline decoration-emerald-300 hover:decoration-emerald-500 transition-colors">info@rentsparks.com</a> if you'd like our vetted vendor list or our property management checklist as a downloadable PDF.
            </p>
          </div>
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {advantages.map((advantage, index) => (
            <AdvantageCard 
              key={index}
              icon={advantage.icon}
              title={advantage.title}
              description={advantage.description}
            />
          ))}
        </div>
      </div>
    </section>
  );
};

export default Advantages;